'use client';

import { useState, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { CatalogCard } from './catalog-card';
import type { CatalogItem, CatalogType } from '@/lib/services/catalog/catalog-types';

// Import the modular components
import { StandardGrid } from './grid-components/standard-grid';
import { TableView } from './grid-components/table-view';
import { GalleryWall } from './grid-components/gallery-wall';
import { MasonryGrid } from './grid-components/masonry-grid';
import { StatusIndicators } from './grid-components/status-indicators';
import { EnlargedArtworkModal } from './grid-components/enlarged-artwork-modal';
import { getApiClient } from '@/lib/api/api-client';
import type { Exhibition } from '@/types';
import { ExhibitionsAPI } from '@/lib/services/catalog/exhibitions.service';
import { hasRichTextContent } from '@/lib/utils/lexical-to-html';

interface CatalogGridProps {
  artworks: CatalogItem[];
  filteredArtworks: CatalogItem[];
  sortOption?: string;
  viewMode?: 'grid4' | 'grid6' | 'grid8' | 'table' | 'gallery';
  catalogType?: CatalogType;
  isLoading?: boolean;
  isError?: boolean;
  hasMore?: boolean;
  isFetchingNextPage?: boolean;
}

export function CatalogGrid({
  artworks: _artworks,
  filteredArtworks,
  sortOption: _sortOption = 'newest',
  viewMode = 'grid6',
  catalogType = 'all',
  isLoading: externalIsLoading,
  isError: _isError,
  hasMore: externalHasMore,
  isFetchingNextPage,
}: CatalogGridProps) {
  const pathname = usePathname();
  const isGridView = viewMode === 'grid4' || viewMode === 'grid6' || viewMode === 'grid8';
  const featuredArtwork = pathname === '/catalog/collections/eastern-promises' && isGridView
    ? filteredArtworks.find((artwork) => String(artwork.id) === '1499')
    : undefined;
  const gridArtworks = featuredArtwork
    ? filteredArtworks.filter((artwork) => String(artwork.id) !== '1499')
    : filteredArtworks;
  const [selectedArtwork, setSelectedArtwork] = useState<CatalogItem | null>(null);
  const [showEnlarged, setShowEnlarged] = useState(false);
  const [copiedWallet, setCopiedWallet] = useState<string | null>(null);

  // Hover states
  const [hoveredArtwork, setHoveredArtwork] = useState<CatalogItem | null>(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });

  const hasMore = externalHasMore ?? true;
  const isLoading = externalIsLoading ?? false;
  const loaderRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // Handle enlarging artwork
  const handleEnlargeArtwork = (artwork: CatalogItem) => {
    setSelectedArtwork(artwork);
    setShowEnlarged(true);
  };

  // Handle copying wallet address
  const handleCopyWallet = (walletAddress: string) => {
    navigator.clipboard.writeText(walletAddress);
    setCopiedWallet(walletAddress);

    // Reset copied state after 2 seconds
    setTimeout(() => {
      setCopiedWallet(null);
    }, 2000);
  };

  // Handle hover events for table view
  const handleMouseEnter = (artwork: CatalogItem) => {
    setHoveredArtwork(artwork);
  };

  const handleMouseLeave = () => {
    setHoveredArtwork(null);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    setHoverPosition({
      x: e.clientX,
      y: e.clientY,
    });
  };

  // Render the appropriate grid component based on the view mode
  const renderContent = () => {
    // Use Masonry Grid for 'all' catalogType (preserves aspect ratio)
    if (catalogType === 'all') {
      switch (viewMode) {
        case 'grid4':
          return (
            <MasonryGrid
              artworks={gridArtworks}
              viewMode="grid4"
              columns={4}
              onEnlarge={handleEnlargeArtwork}
              onCopyWallet={handleCopyWallet}
              copiedWallet={copiedWallet}
              catalogType={catalogType}
              useAspectRatio={true}
            />
          );
        case 'grid6':
          return (
            <MasonryGrid
              artworks={gridArtworks}
              viewMode="grid6"
              columns={6}
              onEnlarge={handleEnlargeArtwork}
              onCopyWallet={handleCopyWallet}
              copiedWallet={copiedWallet}
              catalogType={catalogType}
              useAspectRatio={true}
            />
          );
        case 'grid8':
          return (
            <StandardGrid
              artworks={gridArtworks}
              columns={8}
              onEnlarge={handleEnlargeArtwork}
              onCopyWallet={handleCopyWallet}
              copiedWallet={copiedWallet}
              catalogType={catalogType}
              useAspectRatio={true}
            />
          );
        case 'table':
          return (
            <TableView
              artworks={gridArtworks}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onMouseMove={handleMouseMove}
              onCopyWallet={handleCopyWallet}
              copiedWallet={copiedWallet}
              tableRef={tableRef}
              catalogType={catalogType}
            />
          );
        case 'gallery':
          return <GalleryWall artworks={gridArtworks} onEnlarge={handleEnlargeArtwork} catalogType={catalogType} />;
        default:
          return (
            <MasonryGrid
              artworks={gridArtworks}
              columns={4}
              onEnlarge={handleEnlargeArtwork}
              onCopyWallet={handleCopyWallet}
              copiedWallet={copiedWallet}
              catalogType={catalogType}
              useAspectRatio={true}
            />
          );
      }
    } else {
      // Use Masonry Grid for other catalogTypes (preserves aspect ratio)
      switch (viewMode) {
        case 'grid4':
          return (
            <MasonryGrid
              artworks={gridArtworks}
              viewMode="grid4"
              columns={4}
              onEnlarge={handleEnlargeArtwork}
              onCopyWallet={handleCopyWallet}
              copiedWallet={copiedWallet}
              catalogType={catalogType}
              useAspectRatio={true}
            />
          );
        case 'grid6':
          return (
            <MasonryGrid
              artworks={gridArtworks}
              viewMode="grid6"
              columns={6}
              onEnlarge={handleEnlargeArtwork}
              onCopyWallet={handleCopyWallet}
              copiedWallet={copiedWallet}
              catalogType={catalogType}
              useAspectRatio={true}
            />
          );
        case 'grid8':
          return (
            <StandardGrid
              artworks={gridArtworks}
              columns={8}
              onEnlarge={handleEnlargeArtwork}
              onCopyWallet={handleCopyWallet}
              copiedWallet={copiedWallet}
              catalogType={catalogType}
              useAspectRatio={true}
            />
          );
        case 'table':
          return (
            <TableView
              artworks={gridArtworks}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onMouseMove={handleMouseMove}
              onCopyWallet={handleCopyWallet}
              copiedWallet={copiedWallet}
              tableRef={tableRef}
              catalogType={catalogType}
            />
          );
        case 'gallery':
          return <GalleryWall artworks={gridArtworks} onEnlarge={handleEnlargeArtwork} catalogType={catalogType} />;
        default:
          return (
            <MasonryGrid
              artworks={gridArtworks}
              columns={4}
              onEnlarge={handleEnlargeArtwork}
              onCopyWallet={handleCopyWallet}
              copiedWallet={copiedWallet}
              catalogType={catalogType}
              useAspectRatio={true}
            />
          );
      }
    }
  };

  const [exhibitionLoaded, setExhibitionLoaded] = useState(false);
  const [exhibitionHistory, setExhibitionHistory] = useState<Record<string, Exhibition[]> | null>(null);
  useEffect(() => {
    if (catalogType === 'exhibition') {
      ExhibitionsAPI.findExhibitionHistory().then(result => {
        if (result.data && !result.error) {
          setExhibitionHistory(result.data);
        }
        setExhibitionLoaded(true);
      });
    }
  }, [catalogType]);

  return (
    <div className={`w-full relative ${viewMode === 'gallery' ? 'min-h-[calc(-140px + 100dvh)]' : 'min-h-[85vh]'}`}>
      {/* Main content - æ */}

      {catalogType === 'exhibition' ? (
        <div>
          {exhibitionLoaded && exhibitionHistory && (
            <section className="w-full rounded-md bg-white mb-[12px] flex flex-col justify-between relative">
              {/* Exhibition History - List View */}
              <div className="w-full mb-8">
                <h2 className="text-2xl md:text-3xl font-superior uppercase text-black mb-6">EXHIBITIONS</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-0">
                  {Object.entries(exhibitionHistory).map(([typeName, exhibitions]) => (
                    <div key={typeName} className="mb-6">
                      <h4 className="text-[10px] font-mono uppercase font-bold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                        [ {typeName.toUpperCase()} EXHIBITIONS ]
                      </h4>
                      <div className="space-y-0">
                        {exhibitions.map((exh, idx) => {
                          const year = new Date(exh.duration.startDate).getFullYear();
                          return (
                            <div key={idx} className="grid grid-cols-[40px_1fr_20px] gap-2 py-1 border-b border-gray-100 hover:bg-gray-50 transition-colors group">
                              <span className="text-[10px] font-mono text-gray-500">{year}</span>
                              <div className="text-[10px] font-mono text-gray-700">
                                <span className="font-medium">{exh.title}</span>
                                {exh.event && <span className="text-gray-500">, {exh.event}</span>}
                                {exh.gallery && <span className="text-gray-500">, {exh.gallery}</span>}
                                {(exh.city || exh.country) && (
                                  <span className="text-gray-400">
                                    {' — '}{exh.city}{exh.city && exh.country ? ', ' : ''}{exh.country}
                                  </span>
                                )}
                              </div>
                              {((exh.layout && exh.layout.length > 0) || hasRichTextContent(exh.about) || (exh.description && exh.description.trim().length > 0) || (exh.photos && exh.photos.length > 0) || (exh.videos && exh.videos.length > 0) || (exh.highlights && exh.highlights.length > 0)) && (
                                <a href={`/catalog/exhibitions/${exh.slug}`} className="flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 hover:text-gray-700">
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                    <polyline points="15 3 21 3 21 9" />
                                    <line x1="10" y1="14" x2="21" y2="3" />
                                  </svg>
                                </a>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Featured Exhibitions - Full Width */}
              <div className="w-full">
                <h3 className="text-[10px] font-mono text-gray-900 mb-4">[ FEATURED EXHIBITIONS ]</h3>
                {renderContent()}
              </div>
            </section>
          )}
        </div>
      ) : (
        <div>
          {featuredArtwork && (
            <div className="w-full mb-8" data-featured-artwork="1499">
              <CatalogCard
                artwork={featuredArtwork}
                onEnlarge={handleEnlargeArtwork}
                onCopyWallet={handleCopyWallet}
                copiedWallet={copiedWallet}
                catalogType={catalogType}
                useAspectRatio={true}
              />
            </div>
          )}
          {renderContent()}
        </div>
      )}

      {/* No results message */}
      {filteredArtworks.length === 0 && (
        <div className="flex flex-col items-center justify-end py-16">
          <StatusIndicators.NoResultsIndicator />
        </div>
      )}

      {/* Table hover paints the row. The floating card covered the list. */}

      {/* Enlarged artwork modal */}
      {showEnlarged && selectedArtwork && (
        <EnlargedArtworkModal
          artwork={selectedArtwork}
          onClose={() => setShowEnlarged(false)}
          catalogType={catalogType}
        />
      )}

      {/* Scroll to top button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-white/80 backdrop-blur-sm border border-zinc-200 rounded-full p-3 shadow-md hover:bg-zinc-100 transition-all duration-300 z-50"
          aria-label="Scroll to top"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}

      {/* Bottom spacing */}
      <div className="py-1"></div>
    </div>
  );
}
